import { Box, IconButton } from "@mui/material"
import { MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { MouseEvent, ReactElement } from "react"
import { useRef, useState } from "react"

import WarehouseFormDialog from "../WarehouseEdit/WarehouseFormDialog"

import WarehouseRowDropdownMenu from "./WarehouseRowDropdownMenu"

import { CancelDialog } from "@/components/common/Modals"
import type { WarehouseAreaOutputList } from "@/graphql/codegen/graphql"
import { useGetWarehouse, useUpdateWarehouse } from "@/screens/Warehouses/hooks"

enum WarehouseActionDialogType {
  Form = "FORM",
  ConfirmDeactivate = "CONFIRM_DEACTIVATE",
}

interface WarehouseItemActionsProps {
  row: CellContext<WarehouseAreaOutputList, unknown>["row"]
  onRefresh: () => void
}

const WarehouseRowActions = ({ row, onRefresh }: WarehouseItemActionsProps): ReactElement => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [openedDialog, setOpenedDialog] = useState<WarehouseActionDialogType | null>(null)
  const [editingWarehouseAreaId, setEditingWarehouseAreaId] = useState<string | null>(null)
  const { warehouse } = useGetWarehouse(editingWarehouseAreaId)
  const { update } = useUpdateWarehouse({
    errorMessage: "Failed to update warehouse status.",
    successMessage: "Warehouse status updated successfully.",
    onSuccess: onRefresh,
  })

  const handleStopPropagation = (event: MouseEvent<HTMLElement>) => event.stopPropagation()

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true)
  }
  const handleDropdownClose = () => {
    setIsDropdownOpen(false)
  }

  const updateWarehouseStatus = (isActive: boolean) => {
    update({
      warehouseAreaId: row.original.warehouseAreaId,
      externalSystemId: row.original.externalSystemId!,
      type: row.original.type!,
      isActive,
    })
  }

  const handleStatusToggle = () => {
    setIsDropdownOpen(false)

    if (row.original.isActive) {
      setOpenedDialog(WarehouseActionDialogType.ConfirmDeactivate)
    } else {
      updateWarehouseStatus(true)
    }
  }

  const handleDeactivateConfirm = () => {
    setOpenedDialog(null)
    updateWarehouseStatus(false)
  }

  const handleFormDialogOpen = () => {
    setIsDropdownOpen(false)
    setOpenedDialog(WarehouseActionDialogType.Form)
    setEditingWarehouseAreaId(row.original.warehouseAreaId)
  }

  const handleFormDialogSubmit = () => {
    setOpenedDialog(null)
    onRefresh()
  }

  const handleDialogClose = () => setOpenedDialog(null)

  return (
    <Box className='flex items-center justify-center' ref={anchorElRef} onClick={handleStopPropagation}>
      <IconButton onClick={handleDropdownOpen} data-testid='warehouse-row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <WarehouseRowDropdownMenu
        anchorEl={anchorElRef.current}
        isOpen={isDropdownOpen}
        isActive={row.original?.isActive}
        canChangeStatus={false}
        onClose={handleDropdownClose}
        onStatusToggle={handleStatusToggle}
        onEditDialogOpen={handleFormDialogOpen}
      />
      {openedDialog === WarehouseActionDialogType.ConfirmDeactivate && (
        <CancelDialog
          title='Are you sure you want to deactivate this warehouse?'
          message='By deactivating this warehouse you will be deactivating all associations to this warehouse, so you won’t be able to continue creating any shipping nor billing actions! Click on “Go Back” if this action was a mistake.'
          confirmLabel='Yes, Deactivate'
          onClose={handleDialogClose}
          onConfirm={handleDeactivateConfirm}
        />
      )}
      {openedDialog === WarehouseActionDialogType.Form && warehouse && (
        <WarehouseFormDialog warehouse={warehouse} onSubmit={handleFormDialogSubmit} onClose={handleDialogClose} />
      )}
    </Box>
  )
}

export default WarehouseRowActions
